import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { diff, jsonPatchPathConverter } from 'just-diff';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { LISTINGS } from '../const';
import { MapListingViews } from '../context/MapListingContext';
import { SearchCategory, TypeOfSearch } from '../types/enums';
import { useSearchPropertiesLazyQuery, useSearchPropertiesSeoLazyQuery } from '../types/graphqlHelpers';
import {
  Kfh_PropertySearchMetaType,
  Kfh_PropertyTypeInterface,
  SearchPropertiesQuery,
  SearchPropertiesQueryVariables,
} from '../types/graphqlTypes';
import { PROPERTY_URLS } from '../urls';
import { isNumeric } from '../utils/isNumber';
import { nonFalseValues } from '../utils/nonFalseValues';
import { createQueryString, getQueryString } from '../utils/queryStrings';
import { useCurrencies } from './useCurrencies';
import { useDataLayerPush } from './useDataLayerPush';
import { useSearchMeta } from './useSearchMeta';

export type UseSearchQueryVariables = SearchPropertiesQueryVariables;
// Function to return only items in Set
// Also makes numbers numbers
export const onlyItemsInSet = (
  collection: Record<string, { type: string; isArray: boolean }>,
  items: Record<string, any>
) =>
  Object.fromEntries(
    Object.entries(items)
      // Filter out items that are not in the collection
      .filter(([key]) => Object.prototype.hasOwnProperty.call(collection, key))
      // Correct types
      .map(([key, value]) => {
        const { type, isArray } = collection[key];
        // console.log(key, value, type, isArray);
        let newValue = value;
        // handle 0's
        if (!isArray && type !== 'String' && (newValue === '0' || newValue === 0)) {
          return [key, 0];
        }
        // handle some bad data values
        if ((!newValue && type !== 'Boolean') || (newValue && typeof newValue === 'number' && Number.isNaN(newValue))) {
          return [key, undefined];
        }

        // If number, make it a number
        if (!isArray && type === 'Int' && isNumeric(value)) {
          newValue = parseInt(value as string, 10);
        }
        if (!isArray && type === 'Float' && isNumeric(value)) {
          newValue = parseFloat(value as string);
        }

        // if a boolean, make it a boolean
        if (type === 'Boolean' && typeof value === 'string' && (value === 'true' || value === 'false')) {
          newValue = value === 'true';
        }
        if (type === 'Boolean' && typeof value === 'boolean') {
          newValue = value;
        }

        // if a string, make it a string
        if (!isArray && type === 'String' && typeof value !== 'string') {
          newValue = value.toString();
        }

        if (isArray && type === 'String' && Array.isArray(newValue)) {
          newValue = newValue.map((v) => v.toString());
        }

        // if DateTime, make it a DateTime
        if (type === 'DateTime' && typeof value === 'string') {
          newValue = new Date(value).toISOString();
        }

        // if not an array but should be?
        if (isArray && (newValue === '0' || newValue === 0 || !Array.isArray(newValue))) {
          // newValue = [...newValue.split(',')];
          newValue = [newValue.toString()];
        }

        // because bedroomsMinimum is a number and 0 is for studios
        // if (key === 'bedroomsMinimum' && !value && value !== 0 && value !== '0') {
        //   newValue = undefined;
        // }
        // if (key === 'bedrooms' && !value && value !== 0 && value !== '0') {
        //   newValue = undefined;
        // }

        // Check we don't have any NaN values
        if (Number.isNaN(newValue)) {
          return [key, undefined];
        }

        // return pair
        return [key, newValue];
      })
  );

// Default form values for search
export const defaultValuesShared: Partial<UseSearchQueryVariables> = {
  currencyid: 1,
  first: LISTINGS.perPage,
  page: 1,
  sort: 'HIGHEST',
  nearme: false,
  radius: 5,
  multiSearch: undefined,
  lat: undefined,
  long: undefined,
};

export const defaultValuesCommercial: Partial<UseSearchQueryVariables> = {
  ...defaultValuesShared,
  commercialSizemax: 2500,
  commercialSize: 0,
  category: SearchCategory.Resale,
  type: TypeOfSearch.Commercial,
  commercialArea: '',
  commercialPropertyType: '',
};

export const defaultValuesResidential: Partial<UseSearchQueryVariables> = {
  ...defaultValuesShared,
  underoffer: true,
  newhomes: true,
  onlynewhomes: false,
  category: SearchCategory.Resale,
  type: TypeOfSearch.Residential,
  riverside: false,
  unavailable: false,
  priceLowest: 0,
  priceHighest: 100000000,
  bedrooms: [],
  propertyStyle: [],
  bathrooms: [],
  propertyType: [],
  receptions: [],
  tenure: [],
  furnishState: [],
  areas: [],
};

export const defaultValuesRental: Partial<UseSearchQueryVariables> = {
  ...defaultValuesResidential,
  category: SearchCategory.Rental,
  kfh: true,
  longlet: true,
  shortlet: true,
};
// White list of search params with map for querystring/form/gql values

export type SearchParams = Record<string, { type: string; isArray: boolean }>;
export const searchParamsWhitelistShared: SearchParams = {
  sort: { type: 'String', isArray: false },
  category: { type: 'String', isArray: false },
  first: { type: 'Int', isArray: false },
  page: { type: 'Int', isArray: false },
  type: { type: 'TypeOfSearch', isArray: false },
  lat: { type: 'Float', isArray: false },
  long: { type: 'Float', isArray: false },
  nearme: { type: 'Boolean', isArray: false },
};

export const searchParamsWhitelist: SearchParams = {
  ...searchParamsWhitelistShared,
  age: { type: 'String', isArray: true },
  areas: { type: 'String', isArray: true },
  bathrooms: { type: 'String', isArray: true },
  bedrooms: { type: 'String', isArray: true },
  // bedroomsMinimum: { type: 'Int', isArray: false },
  currencyid: { type: 'Int', isArray: false },
  // lastBuildDateTime: { type: 'DateTime', isArray: false },
  multiSearch: { type: 'String', isArray: false },
  newhomes: { type: 'Boolean', isArray: false },
  onlynewhomes: { type: 'Boolean', isArray: false },
  priceHighest: { type: 'Int', isArray: false },
  priceLowest: { type: 'Int', isArray: false },
  propertyStyle: { type: 'String', isArray: true },
  propertyType: { type: 'String', isArray: true },
  receptions: { type: 'String', isArray: true },
  riverside: { type: 'Boolean', isArray: false },
  kfh: { type: 'Boolean', isArray: false },
  longlet: { type: 'Boolean', isArray: false },
  shortlet: { type: 'Boolean', isArray: false },
  tenure: { type: 'String', isArray: true },
  furnishState: { type: 'String', isArray: true },
  unavailable: { type: 'Boolean', isArray: false },
  underoffer: { type: 'Boolean', isArray: false },
};

export const searchParamsWhitelistCommercial: SearchParams = {
  ...searchParamsWhitelistShared,
  commercialSize: { type: 'Int', isArray: false },
  commercialSizemax: { type: 'Int', isArray: false },
  commercialArea: { type: 'String', isArray: false },
  commercialPropertyType: { type: 'String', isArray: false },
};

interface UseSearchProps {
  queryString?: string;
  overrideValues?: Record<string, any>;
  multiSearchName?: string;
  activeView?: MapListingViews;
  disableAutoSearch?: boolean;
  searchFirstRunDefault?: boolean;
  lastBuild?: Date;
  seoOnly?: boolean;
}
interface UseSearchReturn {
  searchMeta: Kfh_PropertySearchMetaType;
  handleReset: () => void;
  resetValues: Partial<UseSearchQueryVariables>;
  searchValues: Partial<UseSearchQueryVariables>;
  getProperties: (options) => any;
  data?: SearchPropertiesQuery;
  loading: boolean;
  queryString: string;
  called: boolean;
  error?: any;
  handleOnSubmit: (e: any) => void;
  formMethods: UseFormReturn<UseSearchQueryVariables>;
  page: number;
  defaultValues: Partial<UseSearchQueryVariables>;
  activeFilters: Partial<UseSearchQueryVariables>;
  activeFiltersCount: number;
  seoData: any;
  canonical: string | undefined;
}
export const useSearch = ({
  queryString,
  lastBuild,
  overrideValues,
  multiSearchName,
  activeView,
  disableAutoSearch,
  searchFirstRunDefault = false,
  seoOnly = false,
}: UseSearchProps): UseSearchReturn => {
  const { dataLayerPush } = useDataLayerPush();
  const location = useLocation();
  const { current, currentName: currencyName } = useCurrencies();
  // store the previous search to compare against
  const previousGaEvent = useRef<string | undefined>(undefined);
  const previousSearch = useRef<string | undefined>(undefined);
  const previousSearchObject = useRef<Partial<UseSearchQueryVariables>>({});

  // Ref to store if is first load
  const isFirstLoad = useRef<boolean>(true);

  // Get the querystring values

  const queryStringParsed = useMemo(() => getQueryString(queryString), [queryString]);

  // Make sure they are in our whitelist
  const safeQuery = useMemo(
    () =>
      queryStringParsed
        ? onlyItemsInSet(
            overrideValues?.type === TypeOfSearch.Commercial ? searchParamsWhitelistCommercial : searchParamsWhitelist,
            queryStringParsed
          )
        : {},
    [queryStringParsed, overrideValues]
  );

  // Get the default values we need based ont The type of search
  const defaultValues = useMemo(() => {
    let defaultValuesReturned = defaultValuesResidential;

    if (overrideValues?.type === TypeOfSearch.Commercial) {
      defaultValuesReturned = defaultValuesCommercial;
    }
    if (overrideValues?.category === SearchCategory.Rental) {
      defaultValuesReturned = defaultValuesRental;
    }

    return {
      ...defaultValuesReturned,
      ...overrideValues,
    };
  }, [overrideValues]);

  //  Merge default and querystring values
  const startSearchValues = useMemo(
    () =>
      ({
        ...defaultValues,
        currencyid: current?.remoteId ? +current?.remoteId : 1,
        ...safeQuery,
      } as Partial<UseSearchQueryVariables>),
    [defaultValues, current?.remoteId, safeQuery]
  );
  const [page, setPage] = useState(startSearchValues?.page || 1);

  // Store the search values
  const [searchValues, setSearchValues] = useState(startSearchValues);

  // When the form is 'cleared' we need to reset the values back to these
  const resetValues = {
    ...defaultValues,
    currencyid: current?.remoteId ? +current?.remoteId : 1,
  } as Partial<UseSearchQueryVariables>;

  // Register form
  const formMethods = useForm<UseSearchQueryVariables>({
    defaultValues: startSearchValues,
    mode: 'onChange',
  });

  const { handleSubmit, watch, formState, reset } = formMethods;

  // Get the search options
  const searchMeta = useSearchMeta();
  // Current form data values
  const formData = watch();

  // store the active filters
  const activeFilters = useMemo(() => {
    const items = Object.keys(formData).reduce((acc, key) => {
      if (
        !Number.isNaN(formData[key]) &&
        ((!Array.isArray(formData[key]) && formData[key] !== defaultValues[key]) ||
          (Array.isArray(formData[key]) && formData[key].length >= 1))
      ) {
        acc[key] = formData[key];
      }

      return acc;
    }, {});

    return items;
  }, [defaultValues, formData]);

  // And the count
  const activeFiltersCount = Object.keys(activeFilters).length;

  // Auto generated GQL Hook to get the search results
  const [getProperties, { loading: formLoading, error, data, called }] = useSearchPropertiesLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [getSeo, { data: seoData }] = useSearchPropertiesSeoLazyQuery();

  useEffect(() => {
    const handleSearch = () => {
      const queryStringObj = getQueryString(queryString);

      // Make sure they are in our whitelist
      const theQuery = queryStringObj
        ? onlyItemsInSet(
            overrideValues?.type === TypeOfSearch.Commercial ? searchParamsWhitelistCommercial : searchParamsWhitelist,
            queryStringObj
          )
        : {};

      const variables = {
        ...defaultValues,
        ...theQuery,

        lastBuildDateTime: lastBuild,
      };
      if (seoOnly) {
        getSeo({ variables });
      } else {
        getProperties({ variables });
      }
      reset({ ...defaultValues, ...theQuery });
    };

    if (!disableAutoSearch && location.pathname === '/search-results/') {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, disableAutoSearch]);

  // When Search is updated fire GA events.
  useEffect(() => {
    if (!seoOnly && called && data) {
      // Data layer

      const searchParametersRaw = {
        ...formData,
      };

      // loop over object and change empty arrays to null
      const searchParameters = Object.keys(searchParametersRaw).reduce((acc, key) => {
        if (Array.isArray(searchParametersRaw[key]) && searchParametersRaw[key].length === 0) {
          acc[key] = undefined;
        } else {
          acc[key] = searchParametersRaw[key];
        }

        return acc;
      }, {});

      const returnData: Record<string, any> = {
        searchParameters: { ...searchParameters },
      };

      const diffArray = diff(previousSearchObject.current, searchParameters, jsonPatchPathConverter);

      const isPageChange =
        !isFirstLoad.current && diffArray.length === 1 && diffArray.some((item) => item.path === '/page');

      previousSearchObject.current = searchParameters;

      // don't want to fire anything on page or sort order changes
      if (!isPageChange) {
        // Also its not an update on first load
        if (!isFirstLoad.current && diffArray.length > 0) {
          const [reportChanges, ...rest] = diffArray.filter((item) => item.path !== '/page');
          if (reportChanges) {
            returnData.searchUpdateField = reportChanges.path.replace(/^\/|\/\d+$/g, '');
            if (Array.isArray(reportChanges.value)) {
              returnData.searchUpdateValue = reportChanges.value.join(', ');
            } else {
              returnData.searchUpdateValue = reportChanges.value;
            }
          }
        }
        // append total results to GA data
        returnData.totalSearchResults = data?.properties?.propertySearch?.properties?.totalCount;

        returnData.searchParameters.currency = currencyName;
        const testData = JSON.stringify(returnData, null, 0);
        // Stop duplicates
        if (previousGaEvent.current !== testData) {
          if (isFirstLoad.current) {
            dataLayerPush({
              event: 'propertySearch',
              ...returnData,
            });
          } else {
            dataLayerPush({
              event: 'propertySearchUpdate',
              ...returnData,
            });
          }

          previousGaEvent.current = testData;
        }
      }

      isFirstLoad.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [called, data]);

  // If we are not on the search-results page we need to navigate to it
  const setQueyStringTo = (qs) => {
    navigate(`/${PROPERTY_URLS.searchResults}?${qs}`, {
      state: { multiSearchName, multiSearch: startSearchValues?.multiSearch, activeView, searchFirstRunDefault },
    });
  };

  // Handle the form submit to search for properties by setting the query string
  const handleOnSubmitAction: SubmitHandler<UseSearchQueryVariables> = (submitFormData) => {
    const variables = {
      ...defaultValues,
      ...submitFormData,
      lastBuildDateTime: lastBuild,
    };
    // if (submitFormData.bedroomsMinimum >= 0) {
    //   variables.bedrooms = [];
    // }
    // if the page doesn't change then its a filter and need to go back to 1 else we can keep the page
    if (!isFirstLoad.current && submitFormData?.page === page && submitFormData?.page !== 1) {
      variables.page = 1;
      formMethods.setValue('page', 1);
    }

    // Create new Query string
    const whitelist =
      variables.type === TypeOfSearch.Commercial ? searchParamsWhitelistCommercial : searchParamsWhitelist;
    const queryStringValues = onlyItemsInSet(whitelist, nonFalseValues({ variables, defaultValues: {} }));

    const qs = createQueryString(queryStringValues);

    setSearchValues(queryStringValues);
    setPage(variables.page || 1);
    if (!seoOnly) {
      setQueyStringTo(qs);
    }
  };
  const handleOnSubmit = handleSubmit(handleOnSubmitAction);

  // form handler to reset everything back to the default
  const handleReset = () => {
    formMethods.reset(resetValues);

    handleOnSubmitAction(resetValues as UseSearchQueryVariables);
  };

  // Auto submit form on change
  useEffect(() => {
    function replacer(key, value) {
      // Filtering out properties
      // if (key === 'bedroomsMinimum' && !value && value !== 0) {
      //   return undefined;
      // }
      if (key === 'bedrooms' && !value && value !== 0 && value !== '0') {
        return undefined;
      }
      return value;
    }

    const checkData = JSON.stringify(formData, replacer, 0);

    if (
      !disableAutoSearch &&
      (formState.isDirty || queryString) &&
      formData &&
      previousSearch.current &&
      previousSearch.current !== checkData
    ) {
      previousSearch.current = checkData;
      handleOnSubmit();
    } else {
      previousSearch.current = checkData;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleOnSubmit, disableAutoSearch, formData]);

  // useEffect(() => {
  //   const subscription = watch((values, { name, type }) => {
  //     console.log({ values, name, type });
  //   });
  //   return () => subscription.unsubscribe();
  // }, [watch]);

  // Show spinner if as soon as form is submitted and while XHR request is in progress
  const loading = (called && formLoading) || formState.isSubmitting;

  // seo data
  const formattedSeoData = seoData?.properties?.propertySearch?.seo || data?.properties?.propertySearch?.seo;
  const canonicalString =
    seoData?.properties?.propertySearch?.canonicalSearch?.url || data?.properties?.propertySearch?.canonicalSearch?.url;
  const fallbackSearch =
    seoData?.properties?.propertySearch?.fallbackSearch?.fallbackSearch ||
    data?.properties?.propertySearch?.fallbackSearch?.fallbackSearch ||
    false;
  const canonical = !fallbackSearch && canonicalString ? canonicalString : undefined;

  return {
    defaultValues,
    searchMeta,
    resetValues,
    handleReset,
    searchValues,
    queryString: createQueryString(searchValues),
    getProperties,
    loading,
    called,
    error,
    data,
    handleOnSubmit,
    formMethods,
    page,
    activeFilters,
    activeFiltersCount,
    seoData: formattedSeoData,
    canonical,
  };
};
